import { IconButton, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useStyles } from './MessagePreview.styles';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useEffect, useRef, useState } from 'react';
import { deleteMessage, getMessageContent, getTextWithQuotAndSpace } from '../../../services/communication/communication.service';
import { AxiosResponse } from 'axios';
import { mailboxActions } from '../../../reducers/mailboxReducer/mailboxSlice';
import { useLayoutContext } from '../../../Context/Layout.context';
import { convertToFormatDateTime } from '../../../utils/dateFunctionUtils';
import OutlinedButton from '../../buttons/OutlinedButton.component';
import { ReactComponent as ReplyIcon } from '../../../assets/icons/reply.svg';
import { ReactComponent as ForwardIcon } from '../../../assets/icons/forward.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import {
  FlagId,
  FolderMessageContent,
  foldersWithDeleteEnabled,
  foldersWithForwardEnabled,
  foldersWithSeparatedParticipant,
  foldersWithSpecialCaseDataDisplaying,
  Priority,
} from '../../../models/communication.types';
import PriorityBox from '../PriorityBox/PriorityBox.component';
import MessageFlag from '../MessageFlag.component';
import DropdownMenu, { MenuItemWithImage } from '../../DropdownMenu/DropdownMenu.component';
import ClearIcon from '@mui/icons-material/Clear';
import MessageParticipantLabel from '../MessagesList/MessageParticipantLabel.component';
import { Prompt } from '../../Prompt/Prompt.component';
import MessagePreviewBodyFooter from '../MessagePreviewBodyFooter/MessagePreviewBodyFooter.component';
import { handleRemoveMessageFlag, handleSetMessageFlag } from '../MessagesList/utils';
import { getAssessment, getSpecialCaseCardRedirectorUrl } from '../../../utils/routerUrls';
import { useHistory } from 'react-router-dom';
import { fill } from '../../../utils/contrastSvgAttributes';

const MessagePreview: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { openAlert } = useLayoutContext();
  const prevMessageId = useRef<string | null>(null);
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);
  const activeMessageId = useSelector((state: RootState) => state.mailbox.activeMessageId);
  const replyMessageId = useSelector((state: RootState) => state.mailbox.replyMessageId);
  const forwardMessageId = useSelector((state: RootState) => state.mailbox.forwardMessageId);
  const actionsHidden = replyMessageId || forwardMessageId;
  const loadedMessageContent = useSelector((state: RootState) => state.mailbox.loadedMessageContent);

  const actionMenuRef = useRef<SVGSVGElement | null>(null);
  const history = useHistory();

  const [actionMenuAnchor, setActionMenuAnchor] = useState<SVGSVGElement | undefined>(undefined);
  const [messageDeletingConfirmPrompt, setMessageDeletingConfirmPrompt] = useState<boolean>(false);

  const forwardingEnabled = foldersWithForwardEnabled.includes(activeFolder);

  useEffect(() => {
    if (activeMessageId && (activeMessageId !== prevMessageId.current || !loadedMessageContent)) {
      dispatch(mailboxActions.setPrevMessageId(prevMessageId.current));
      prevMessageId.current = activeMessageId;
      getMessageContent(activeMessageId).then(
        (response: AxiosResponse<FolderMessageContent>) => {
          dispatch(mailboxActions.loadMessage(response.data));
        },
        () => {
          openAlert('get_error');
        },
      );
    }
  }, [activeMessageId, loadedMessageContent]);

  if (!loadedMessageContent) {
    return <></>;
  }

  const handleReply = () => {
    dispatch(mailboxActions.openReplyForMessage(activeMessageId));
  };

  const handleForward = () => {
    dispatch(mailboxActions.openForwardForMessage(activeMessageId));
  };

  const handleUpdateCurrentMessageFlag = (flag: FlagId | null): void => {
    dispatch(mailboxActions.updateCurrentMessageFlag(flag));
    if (activeMessageId) {
      dispatch(mailboxActions.setMessageToUpdateFlag({ messageId: activeMessageId, flag: flag }));
    }
  };

  const actionItems: MenuItemWithImage[] = [
    {
      text: 'Odpowiedz',
      icon: <ReplyIcon fill={fill} />,
      onClick: handleReply,
    },
    ...(forwardingEnabled
      ? [
          {
            text: 'Przekaż dalej',
            icon: <ForwardIcon fill={fill} />,
            onClick: handleForward,
          },
        ]
      : []),
    ...(foldersWithDeleteEnabled.includes(activeFolder)
      ? [
          {
            text: 'Usuń wiadomość',
            icon: <TrashIcon fill={fill} />,
            onClick: () => setMessageDeletingConfirmPrompt(true),
          },
        ]
      : []),
    {
      text: 'Oznacz jako ważną',
      icon: <MessageFlag flagId={FlagId.Green} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Green, handleUpdateCurrentMessageFlag, openAlert);
        setActionMenuAnchor(undefined);
      },
    },
    {
      text: 'Oznacz jako bardzo ważną',
      icon: <MessageFlag flagId={FlagId.Yellow} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Yellow, handleUpdateCurrentMessageFlag, openAlert);
        setActionMenuAnchor(undefined);
      },
    },
    {
      text: 'Oznacz jako pilną',
      icon: <MessageFlag flagId={FlagId.Red} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Red, handleUpdateCurrentMessageFlag, openAlert),
          setActionMenuAnchor(undefined);
      },
    },
    {
      disabled: loadedMessageContent.flag === null,
      text: 'Usuń oznaczenie',
      icon: <ClearIcon />,
      onClick: () => {
        handleRemoveMessageFlag(activeMessageId, handleUpdateCurrentMessageFlag, openAlert),
          setActionMenuAnchor(undefined);
      },
    },
  ];

  const handleDeleteMessage = (messageId: string) => {
    deleteMessage(messageId)
      .then(
        () => {
          openAlert('success', 'Wiadomość została pomyślnie usunięta');
          dispatch(mailboxActions.addMessageToDelete(messageId));
        },
        () => openAlert('error', 'Usunięcie wiadomości nie powiodło się. Spróbuj ponownie później'),
      )
      .finally(() => {
        setMessageDeletingConfirmPrompt(false);
      });
  };

  return (
    <div className={classes.messagePreview}>
      <div
        className={classes.messageContent}
        style={{
          height: actionsHidden ? '100%' : 'calc(100% - 45px)',
        }}
      >
        <div className={classes.subjectContener}>
          <Typography className={classes.subject}>{getTextWithQuotAndSpace(loadedMessageContent.subject)}</Typography>
        </div>
        {loadedMessageContent.priorityId !== Priority.Low && (
          <div style={{ padding: '0 15px 0 15px', marginBottom: '-15px' }}>
            <PriorityBox priority={loadedMessageContent.priorityId} allreadySent={true} />
          </div>
        )}
        <div className={classes.messageContener}>
          <div className={classes.messageHeader}>
            <div className={classes.messageHead}>
              <div style={{ flexGrow: 2 }}>
                <Typography className={classes.sender}>
                  <MessageParticipantLabel message={loadedMessageContent} />
                </Typography>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <Typography className={classes.recipient}>
                    <MessageParticipantLabel message={loadedMessageContent} revert={true} />
                  </Typography>
                </div>
              </div>
              <div>
                <Typography className={classes.sentDate}>
                  {convertToFormatDateTime(loadedMessageContent.sentDate)}
                </Typography>
              </div>
              {loadedMessageContent.flag !== null && (
                <div style={{ margin: '0 -3px 0 5px' }}>
                  <MessageFlag flagId={loadedMessageContent.flag} />
                </div>
              )}
              {!actionsHidden && (
                <div>
                  <IconButton
                    disableRipple
                    aria-label="Akcje"
                    size="small"
                    onClick={() => setActionMenuAnchor(actionMenuRef.current ?? undefined)}
                  >
                    <MoreVertIcon
                      ref={actionMenuRef}
                      className={classes.actionIcon}
                      aria-controls="action-menu"
                      aria-haspopup="true"
                      aria-hidden="false"
                    />
                  </IconButton>
                  <DropdownMenu
                    anchorEl={actionMenuAnchor}
                    setAnchorEl={setActionMenuAnchor}
                    menuItemsWithImages={actionItems}
                    onClick={(text, item) => {
                      item && item.onClick && item.onClick();
                      setActionMenuAnchor(undefined);
                    }}
                    onClose={() => setActionMenuAnchor(undefined)}
                    placement="left-start"
                  />
                </div>
              )}
            </div>
            {foldersWithSeparatedParticipant.includes(activeFolder) &&
              loadedMessageContent.examTaskGroupSolutionScoreId &&
              loadedMessageContent.assessmentSession && (
                <div className={classes.buttonContainer}>
                  <OutlinedButton
                    className={classes.goToCardButton}
                    size="small"
                    onClick={() =>
                      history.push(
                        getAssessment(
                          loadedMessageContent.examTaskGroupSolutionScoreId!,
                          loadedMessageContent.assessmentSession,
                        ),
                      )
                    }
                  >
                    Wyświetl pracę
                  </OutlinedButton>
                </div>
              )}

            {foldersWithSpecialCaseDataDisplaying.includes(activeFolder) &&
              loadedMessageContent.examTaskGroupSolutionScoreId &&
              loadedMessageContent.taskSolutionSheetId && (
                <div className={classes.specialCaseRow}>
                  <div style={{ flexGrow: 2 }}>
                    <Typography className={classes.specialCaseType}>
                      <span className={classes.specialCaseTypeLabel}>Rodzaj:</span>{' '}
                      {loadedMessageContent.specialCaseDefinitionTitle}
                    </Typography>
                  </div>
                  <div>
                    <OutlinedButton
                      className={classes.goToCardButton}
                      size="small"
                      onClick={() =>
                        history.push(
                          getSpecialCaseCardRedirectorUrl(
                            loadedMessageContent.examTaskGroupSolutionScoreId!,
                            loadedMessageContent.taskSolutionSheetId!,
                            loadedMessageContent.assessmentSession,
                          ),
                        )
                      }
                    >
                      Wyświetl pracę
                    </OutlinedButton>
                  </div>
                </div>
              )}
          </div>

          <div style={{ height: '100%', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            <Typography className={classes.content}>{getTextWithQuotAndSpace(loadedMessageContent.body)}</Typography>
          </div>
          {loadedMessageContent && <MessagePreviewBodyFooter messageContent={loadedMessageContent} />}
        </div>
      </div>
      {!actionsHidden && (
        <div className={classes.buttonsFooter}>
          <OutlinedButton startIcon={<ReplyIcon fill={fill} title="ikona odpowiedz" />} onClick={handleReply}>
            Odpowiedz
          </OutlinedButton>
          {forwardingEnabled && (
            <OutlinedButton
              endIcon={<ForwardIcon fill={fill} title="ikona odpowiedz" />}
              style={{ marginLeft: '10px' }}
              onClick={handleForward}
            >
              Przekaż dalej
            </OutlinedButton>
          )}
        </div>
      )}
      {messageDeletingConfirmPrompt && (
        <Prompt
          type="confirmation"
          mainText="Czy na pewno chcesz usunąć wiadomość?"
          mainActionButtonText="TAK"
          onConfirm={() => handleDeleteMessage(loadedMessageContent.messageId)}
          onCancel={() => setMessageDeletingConfirmPrompt(false)}
        />
      )}
    </div>
  );
};

export default MessagePreview;
